import React, { useContext } from "react";
import clsx from "clsx";

import { Skeleton, EmptyBlock } from "components";
import { TablePagination } from "components/tablePagination";
import { permissionList, useRequiredPermissions } from "helpers";
import { useLoader } from "hooks";
import { AnyNote, NoteMutation } from "types";

import { NoteRow } from "./noteRow";
import NoteServiceContext from "./notesServiceContext";
import useNoteService from "./useNoteService";

import styles from "./notesTable.module.scss";

export type NotesTableProps = {
	entityId: string | number;
	filters?: any;
	readOnly?: boolean;
	isMainHeightForNoRecordsBlock?: boolean;
	isShouldPermissionsBeApplied?: boolean;
	sectionTitle?: string;
	onViewNote: Function;
	onEditNote: Function;
	onDeleteNote: Function;
	contentStyle?: React.CSSProperties;
	AddNoteButton?: React.FC<{ addNote: NoteMutation }>;
	AddPrivateNoteButton?: React.FC<{ addNote: NoteMutation }>;
	dealNotesCategories?: { [key: string]: any };
	className?: string;
	onSuccessDeleteNote?: Function;
	onSuccessEditNote?: Function;
	onSuccessAddNote?: Function;
};

const NotesTable = ({
	entityId,
	filters,
	sectionTitle,
	readOnly,
	onViewNote,
	onEditNote,
	onDeleteNote,
	contentStyle,
	AddNoteButton,
	AddPrivateNoteButton,
	isMainHeightForNoRecordsBlock = true,
	isShouldPermissionsBeApplied = false,
	dealNotesCategories,
	className,
	onSuccessDeleteNote,
	onSuccessEditNote,
	onSuccessAddNote,
}: NotesTableProps) => {
	const hasAccountNotePermissions = useRequiredPermissions(
		permissionList.AddAccountNotes,
	);

	const hasPrivateNotesUwOnlyPermissions = useRequiredPermissions(
		permissionList.ViewPrivateNotesUwOnly,
	);

	const isNonPermissions = isShouldPermissionsBeApplied
		? isShouldPermissionsBeApplied
		: hasAccountNotePermissions;

	const { key, service } = useContext(NoteServiceContext);
	const {
		useNoteListQuery,
		useAddNoteMutation,
		useEditNoteMutation,
		useDeleteNoteMutation,
	} = useNoteService({ key, service });

	const { data, isFetching, page, setPage, refetch } = useNoteListQuery({
		entityId,
		...filters,
	});

	const { mutateAsync: addNote } = useAddNoteMutation({
		onSuccessCb: (newNote: AnyNote | undefined) => {
			refetch();
			onSuccessAddNote?.(newNote);
		},
	});

	const { mutate: editNote } = useEditNoteMutation({
		onSuccessCb: () => {
			refetch();
			onSuccessEditNote?.();
		},
	});

	const { mutate: deleteNote } = useDeleteNoteMutation({
		onSuccessCb: () => {
			refetch();
			onSuccessDeleteNote?.();
		},
	});

	const getNoteData = note => {
		if (dealNotesCategories && note?.category !== undefined) {
			return {
				...note,
				category: dealNotesCategories.getByValue(note.category),
			};
		}
		return note;
	};

	const Loader = useLoader(isFetching);

	const isNoRecordsClassName = isMainHeightForNoRecordsBlock
		? styles.mainHeightNoRecordsBlock
		: styles.minHeightNoRecordsBlock;

	return (
		<div className={`${styles.tableBlock} ${className || ""}`}>
			<div className={styles.wrappTable}>
				<div className={styles.tableActions}>
					{sectionTitle && (
						<h6 className={styles.sectionTitle}>{sectionTitle}</h6>
					)}
					{!readOnly && isNonPermissions && AddNoteButton && (
						<AddNoteButton addNote={addNote} />
					)}
					{!readOnly &&
						hasAccountNotePermissions &&
						hasPrivateNotesUwOnlyPermissions &&
						AddPrivateNoteButton && (
							<AddPrivateNoteButton addNote={addNote} />
						)}
				</div>
				{data?.items?.length === 0 && !isFetching ? (
					<div
						className={clsx(
							styles.wrappNoRecordsBlock,
							isNoRecordsClassName,
						)}
					>
						<EmptyBlock />
					</div>
				) : (
					<Loader
						skeleton={<Skeleton height="calc(100vh - 395px)" />}
						component={
							<div
								className={styles.wrappBodyTable}
								style={contentStyle}
							>
								{data?.items?.map(item => {
									let note = getNoteData(item);
									return (
										<NoteRow
											key={note.id}
											note={note}
											isPermissions={
												isShouldPermissionsBeApplied
											}
											onViewNote={() =>
												onViewNote({
													note,
												})
											}
											onEditNote={() =>
												onEditNote({
													note,
													editNote,
												})
											}
											onDeleteNote={() =>
												onDeleteNote({
													note,
													deleteNote,
												})
											}
										/>
									);
								})}
							</div>
						}
					/>
				)}
			</div>
			<Loader
				skeleton={<Skeleton height="51px" marginTop="5px" />}
				component={
					data?.items?.length > 0 && (
						<TablePagination
							page={page}
							count={data?.count}
							handlePageChange={(page: number) =>
								setPage(page - 1)
							}
						/>
					)
				}
			/>
		</div>
	);
};

export default NotesTable;
